import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const getBackendUrl = () => process.env.REACT_APP_BACKEND_URL
  || process.env.REACT_APP_API_URL
  || process.env.REACT_APP_API_BASE
  || (process.env.NODE_ENV === 'production' && typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
const API_BASE = getBackendUrl();
const RELATIONSHIPS = [
  ['spouse_partner', 'Spouse / Partner'],
  ['parent', 'Parent'],
  ['child', 'Child'],
  ['sibling', 'Sibling'],
  ['friend', 'Friend'],
  ['ride_driver', 'Ride / Driver'],
  ['caregiver', 'Caregiver'],
  ['other', 'Other'],
];

export default function PostoperativeContactSharePage({ token }) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expired, setExpired] = useState(false);
  const [error, setError] = useState(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [relationshipCode, setRelationshipCode] = useState('');
  const [relationshipOther, setRelationshipOther] = useState('');
  const [authorizationConfirmed, setAuthorizationConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid link.');
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/api/postoperative-contact-share/${encodeURIComponent(token)}`, { cache: 'no-store' })
      .then(async (response) => {
        const payload = await response.json();
        if (response.status === 410) {
          setExpired(true);
          return;
        }
        if (!response.ok) throw new Error(payload.error || 'Unable to load this link.');
        setContext(payload.data || payload);
      })
      .catch((err) => setError(err.message || 'Unable to load this link.'))
      .finally(() => setLoading(false));
  }, [token]);

  const patientMode = context?.mode === 'patient';
  const digits = phone.replace(/\D/g, '');
  const valid = name.trim() && (digits.length === 10 || (digits.length === 11 && digits.startsWith('1')))
    && relationshipCode && (relationshipCode !== 'other' || relationshipOther.trim())
    && (!patientMode || authorizationConfirmed);

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/postoperative-contact-share/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone,
          relationshipCode,
          relationshipOther: relationshipCode === 'other' ? relationshipOther.trim() : null,
          authorizationConfirmed: patientMode ? authorizationConfirmed : false,
        }),
      });
      const payload = await response.json();
      if (response.status === 410) {
        setExpired(true);
        return;
      }
      if (!response.ok) throw new Error(payload.error || 'Unable to submit contact information.');
      setSubmitted(payload.data || payload);
    } catch (err) {
      setError(err.message || 'Unable to submit contact information.');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setName('');
    setPhone('');
    setRelationshipCode('');
    setRelationshipOther('');
    setAuthorizationConfirmed(false);
    setSubmitted(null);
    setError(null);
  };

  if (loading) return <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}><CircularProgress /></Box>;
  if (expired) return <Message title="Link Expired">This secure link is no longer available. Please ask the surgical team for a new link.</Message>;
  if (!context) return <Message title="Unable to Open Link" error={error}>{error || 'This secure link is unavailable.'}</Message>;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fb', px: 2, py: { xs: 3, sm: 6 } }}>
      <Paper elevation={0} sx={{ maxWidth: 520, mx: 'auto', borderRadius: 3, overflow: 'hidden', border: '1px solid #dbe5f1' }}>
        <Box sx={{ bgcolor: '#1565c0', color: '#fff', px: 3, py: 2.5 }}>
          <Typography variant="overline" sx={{ opacity: 0.85 }}>{context.practiceName}</Typography>
          <Typography variant="h5" fontWeight={700}>Postoperative Contact</Typography>
          {context.patientFirstName && <Typography sx={{ opacity: 0.9, mt: 0.5 }}>For {context.patientFirstName}</Typography>}
        </Box>
        <Box sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          {submitted ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 58 }} />
              <Typography variant="h5" fontWeight={700} mt={1}>Information received</Typography>
              <Typography color="text.secondary" mt={1}>
                {submitted.requiresStaffApproval
                  ? 'The surgical team will confirm the patient’s authorization before sharing postoperative updates.'
                  : 'This contact is now available to the surgical team for postoperative updates.'}
              </Typography>
              <Button variant="outlined" onClick={reset} sx={{ mt: 3 }}>Add another contact</Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={submit}>
              <Typography color="text.secondary" mb={2.5}>
                {patientMode
                  ? 'Enter the person or people who may receive an update after surgery.'
                  : 'Enter your information so the surgical team can contact you after surgery. Staff must confirm the patient’s authorization first.'}
              </Typography>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              <TextField fullWidth required label="Contact name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" sx={{ mb: 2 }} />
              <TextField fullWidth required label="Mobile phone" value={phone} onChange={(e) => setPhone(e.target.value)} type="tel" autoComplete="tel" inputProps={{ inputMode: 'tel' }} sx={{ mb: 2 }} />
              <FormControl fullWidth required sx={{ mb: 2 }}>
                <InputLabel id="relationship-label">Relationship or role</InputLabel>
                <Select labelId="relationship-label" label="Relationship or role" value={relationshipCode} onChange={(e) => setRelationshipCode(e.target.value)}>
                  {RELATIONSHIPS.map(([value, label]) => <MenuItem value={value} key={value}>{label}</MenuItem>)}
                </Select>
              </FormControl>
              {relationshipCode === 'other' && (
                <TextField fullWidth required label="Describe relationship" value={relationshipOther} onChange={(e) => setRelationshipOther(e.target.value)} sx={{ mb: 2 }} />
              )}
              {patientMode && (
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', mx: 0, mb: 2 }}
                  control={<Checkbox checked={authorizationConfirmed} onChange={(e) => setAuthorizationConfirmed(e.target.checked)} sx={{ mt: -0.5 }} />}
                  label="I am the patient and authorize postoperative updates to this person for this surgical encounter."
                />
              )}
              <Button fullWidth size="large" type="submit" variant="contained" disabled={!valid || submitting} sx={{ minHeight: 48 }}>
                {submitting ? <CircularProgress size={23} color="inherit" /> : 'Submit Contact'}
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={2}>
                This secure link expires {new Date(context.expiresAt).toLocaleString()}.
              </Typography>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

function Message({ title, children, error }) {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f7fb', display: 'grid', placeItems: 'center', px: 2 }}>
      <Paper elevation={0} sx={{ maxWidth: 480, p: 4, textAlign: 'center', borderRadius: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>{title}</Typography>
        <Typography color={error ? 'error' : 'text.secondary'}>{children}</Typography>
      </Paper>
    </Box>
  );
}
